import type { ComputedRef, Ref } from 'vue';
import { isRef } from 'vue';

export type ValidationResult = { error: string | undefined };
type ValidationContext<T = any> = {
  model: T;
  key: keyof T;
};
type ValidationFunc<T = any> = (context: ValidationContext<T>) => ValidationResult;

type ValidateConditionFunc<T = any> = (formModel: T) => boolean | undefined;
export type ValidationSchema<T> = {
  [K in keyof T]?: ValidationFunc[];
};

export type SchemaValidationResult<T> = {
  [K in keyof T]?: ValidationResult[];
};
export type formatOptions = {
  formatMoney: boolean;
};

const number =
  (fn: (n: number) => boolean, errorMsg: string): ValidationFunc =>
  (context: ValidationContext) => {
    let error = undefined;
    const v = contextValue(context);
    if (!v) {
      error = 'This field is required';
    } else if (!fn(v as number)) {
      error = errorMsg;
    }
    return { error };
  };

const type =
  (type: string): ValidationFunc =>
  (context: ValidationContext) => {
    let error = undefined;
    const v = contextValue(context);
    if (v === undefined) {
      error = 'This field is required';
    } else if (typeof v !== type) error = 'This field must be true or false';
    return { error };
  };

const typeIf =
  <T>(shouldValidate: ValidateConditionFunc<T>, type: string, errMsg = 'This field is required'): ValidationFunc =>
  (context: ValidationContext) => {
    let error = undefined;
    const v = contextValue(context);

    if (shouldValidate(context.model) && v === undefined) {
      error = errMsg;
    } else if (typeof v !== type && v !== undefined) error = 'This field is the wrong type';
    return { error };
  };

const contextValue = <T>(context: ValidationContext<T>, key?: keyof T) => context.model[key ?? context.key];

const requiredIf =
  <T>(shouldValidate: ValidateConditionFunc<T>, errMsg = 'This field is required'): ValidationFunc =>
  (context: ValidationContext) => {
    let error = undefined;
    if (shouldValidate(context.model) && !contextValue(context)) {
      error = errMsg;
    }
    return { error };
  };

const required =
  (errMsg = 'This field is required', allowFalse = false): ValidationFunc =>
  (context: ValidationContext) => {
    let error = undefined;
    const value = contextValue(context);

    if (allowFalse) {
      // When allowFalse is true, only undefined, null, or empty string are invalid
      if (value === undefined || value === null || value === '') {
        error = errMsg;
      }
    } else {
      // Standard falsy check when allowFalse is false
      if (!value) {
        error = errMsg;
      }
    }

    return { error };
  };

const notEmpty =
  (errorMsg = 'This field is required'): ValidationFunc =>
  (context: ValidationContext) => {
    let error = undefined;
    const value = contextValue(context);

    if (!value?.trim()) {
      error = errorMsg;
    }

    return { error };
  };

const requiredArrayIf =
  <T>(shouldValidate: ValidateConditionFunc<T>, errMsg = 'Requires at least 1 item'): ValidationFunc =>
  (context: ValidationContext) => {
    let error = undefined;
    const v = contextValue(context);
    if (shouldValidate(context.model) && (!v || !(v as any[]).length)) {
      error = errMsg;
    }
    return { error };
  };

const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const email: ValidationFunc = (context: ValidationContext) => {
  let error = undefined;
  const v = contextValue(context);
  if (!(typeof v === 'string') || !emailRegex.test(v as string)) {
    error = 'This value is not a valid email address';
  }
  return { error };
};

const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;
const phone =
  (isRequired = true): ValidationFunc =>
  (context: ValidationContext) => {
    let error = undefined;
    const v = contextValue(context);
    const isEmpty = !v || (v as string).trim().length === 0;

    if (isEmpty && !isRequired) return { error };
    else if (isEmpty && isRequired) {
      error = 'This field is required';
    } else if (!phoneRegex.test(v)) {
      error = 'This value is not a valid phone number';
    }

    return { error };
  };

const match =
  <T>(matchingProp: keyof T): ValidationFunc =>
  (context: ValidationContext) => {
    const value = contextValue(context);
    const matchVal = contextValue(context, matchingProp);
    let error = undefined;
    if (value !== matchVal) {
      error = `This value doesn't match`;
    }
    return { error };
  };

const min =
  <T>(
    minimumValue: number | ComputedRef<number> | Ref<number>,
    fn?: (n: number) => string,
    shouldValidate?: ValidateConditionFunc<T>,
  ): ValidationFunc =>
  (context: ValidationContext) => {
    let error = undefined;
    const v = contextValue(context);
    const curentValue = isRef(minimumValue) ? minimumValue.value : minimumValue;

    if (shouldValidate && !shouldValidate(context.model)) return { error };
    if (typeof v !== 'number' || v < curentValue) {
      if (typeof v === 'number' && fn) {
        error = `This value must be at least ${fn(curentValue)}`;
      } else {
        error = `This value must be at least ${curentValue}`;
      }
    }
    return { error };
  };

const validate = <T>(model: T, schema: ValidationSchema<T>): SchemaValidationResult<T> => {
  const result: SchemaValidationResult<T> = {};
  for (const key in schema) {
    const rules = schema[key];
    const errors = (rules?.map((r) => r({ model, key })) || []).filter((e) => e.error !== undefined);
    if (errors.length) {
      result[key] = errors;
    }
  }
  return result;
};

const oneOf = <T>(values: T[], errorMesssage = ''): ValidationFunc => {
  return (context: ValidationContext) => {
    const value = contextValue(context);
    let error = undefined;

    if (!values.includes(value as T)) {
      error = errorMesssage || `This value must be one of ${values.join(', ')}`;
    }

    return { error };
  };
};

const custom = <T>(shouldValidate: ValidateConditionFunc<T>, validateFn: ValidationFunc<T>): ValidationFunc => {
  return (context: ValidationContext<T>) => {
    if (shouldValidate(context.model)) {
      return validateFn(context);
    }
    return { error: undefined };
  };
};

export {
  type,
  typeIf,
  required,
  email,
  match,
  min,
  validate,
  phone,
  requiredIf,
  number,
  requiredArrayIf,
  notEmpty,
  oneOf,
  custom,
};
